<template>
    <div class="card" :class="[props.class, { 'is-clickable': clickable }]" :style="props.style"
        @click="handleCardClick">
        <template v-if="isVideo">
            <video ref="videoRef" :src="props.url" :alt="props.name" loading="lazy" controlslist="nodownload" muted
                @click.stop="handleVideoClick" @mouseleave="stopPlay" @mouseenter="startPlay" />
            <div v-if="!isPlaying" class="play-btn" @click.stop="handleVideoClick">
                <Play />
            </div>
        </template>
        <img v-else :src="props.url" :alt="props.name" loading="lazy" />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import { useVideoModalStore } from '@/stores/videoModal'
import type { CSSProperties } from 'vue'
import Play from '@/assets/icons/play.svg'

const props = withDefaults(defineProps<{
    url: string
    videoPopup?: boolean
    clickable?: boolean
    name?: string
    style?: CSSProperties
    class?: string
}>(), {
    videoPopup: false,
    clickable: false,
    name: '',
    class: ''
})

const emit = defineEmits<{
    (e: 'click'): void
}>()

const videoModalStore = useVideoModalStore()
const videoRef = ref<HTMLVideoElement | null>(null)
const isPlaying = ref(false)

const isVideo = computed(() => {
    return /\.(mp4|webm|ogg|avi|mov)(\?.*)?$/i.test(props.url)
})

function stopPlay(): void {
    if (props.videoPopup || !isVideo.value) return
    const el = videoRef.value
    if (!el) return
    if (!el.paused) {
        el.pause()
        onVideoPause();
    }
}

function startPlay(): void {
    if (props.videoPopup || !isVideo.value) return
    const el = videoRef.value
    if (!el) return
    if (el.paused) {
        el.play().catch(() => { })
        onVideoPlay()
    }
}

function onVideoEnded(): void {
    isPlaying.value = false
}

function onVideoPause(): void {
    isPlaying.value = false
}

function onVideoPlay(): void {
    isPlaying.value = true
}

function bindVideoEvents(el: HTMLVideoElement): void {
    el.addEventListener('ended', onVideoEnded)
    el.addEventListener('pause', onVideoPause)
    el.addEventListener('play', onVideoPlay)
}

function unbindVideoEvents(el: HTMLVideoElement): void {
    el.removeEventListener('ended', onVideoEnded)
    el.removeEventListener('pause', onVideoPause)
    el.removeEventListener('play', onVideoPlay)
}

watch(videoRef, (newEl, oldEl) => {
    if (oldEl) {
        unbindVideoEvents(oldEl)
    }
    if (newEl) {
        bindVideoEvents(newEl)
    }
})

watch(() => props.url, () => {
    isPlaying.value = false
})

function handleVideoClick(): void {
    if (props.videoPopup) {
        videoModalStore.open({
            src: props.url,
            title: props.name,
            autoplay: true
        })
        return
    } else {
        const el = videoRef.value
        if (!el) return
        if (el.paused) {
            el.play().catch(() => { })
        } else {
            el.pause()
        }
    }
}

function handleCardClick(): void {
    if (isVideo.value) return
    emit('click')
}

onUnmounted(() => {
    const el = videoRef.value
    if (el) {
        unbindVideoEvents(el)
    }
})
</script>

<style lang="scss" scoped>
.card {
    position: relative;
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    &.is-clickable {
        cursor: pointer;
    }

    &>img,
    &>video {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.3s ease, box-shadow 0.3s ease;
    }

    &:hover {

        &>img,
        &>video {
            transform: scale(1.06);
        }
    }
}

.play-btn {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #fff;
    border: none;
    color: #ff6d00;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    transition: transform 0.25s ease, box-shadow 0.25s ease;

    svg {
        width: 22px;
        height: 22px;
        margin-left: 2px;
    }

    @media (min-width: $breakpoint-md) {
        width: 64px;
        height: 64px;

        svg {
            width: 26px;
            height: 26px;
        }
    }

    &:hover {
        transform: translate(-50%, -50%) scale(1.1);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
    }
}
</style>
